import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import "./Positional.css";
import { environment } from "environment/environment";

const POSITIONAL_API_URL = `${environment.stockApiBaseUrl}${environment.positionalStockDailyLevelsUrl}`;

const tableColumns = [
    { key: "symbol", label: "SYMBOL" },
    { key: "ltp", label: "LTP" },
    { key: "high20Date", label: "HIGH 20 DATE" },
    { key: "high5Date", label: "HIGH 5 DATE" },
    { key: "low20Date", label: "LOW 20 DATE" },
    { key: "low5Date", label: "LOW 5 DATE" },
    { key: "weeklyHigh", label: "WEEKLY HIGH" },
    { key: "weeklyLow", label: "WEEKLY LOW" },
    { key: "weeklyClose", label: "WEEKLY CLOSE" },
    { key: "monthlyHigh", label: "MONTHLY HIGH" },
    { key: "monthlyMid", label: "MONTHLY MID" },
    { key: "monthlyLow", label: "MONTHLY LOW" },
    { key: "monthlyClose", label: "MONTHLY CLOSE" },
    { key: "wstatus", label: "W STATUS" },
    { key: "mstatus", label: "M STATUS" },
];

const sortableDateColumns = new Set([
    "high20Date",
    "high5Date",
    "low20Date",
    "low5Date",
]);

const formatValue = (value) => {
    if (value == null || value === "") {
        return "--";
    }

    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split("-");
        return `${day}-${month}-${year}`;
    }

    if (typeof value === "number") {
        return value.toLocaleString("en-IN", {
            minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
            maximumFractionDigits: 2,
        });
    }

    return value;
};

const formatSignedPercent = (value) => {
    if (value == null || Number.isNaN(value)) {
        return "--";
    }

    const numericValue = Number(value);
    const prefix = numericValue > 0 ? "+" : "";

    return `${prefix}${numericValue.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}%`;
};

const getRowsFromPayload = (payload) => {
    if (Array.isArray(payload)) {
        return payload;
    }

    if (Array.isArray(payload?.data)) {
        return payload.data;
    }

    return [];
};

const getDateTimeParts = (value) => {
    const [rawDate = "", rawTime = ""] = String(value || "").trim().split(/\s+/);
    let formattedDate = "--";

    if (rawDate.includes("-")) {
        const [year, month, day] = rawDate.split("-");

        if (year && month && day) {
            formattedDate = `${day}-${month}-${year}`;
        }
    } else if (rawDate) {
        formattedDate = rawDate;
    }

    return {
        date: formattedDate,
        time: rawTime || "--",
    };
};

const getSignalClassName = (signal) => {
    const normalizedSignal = String(signal || "").trim().toUpperCase();

    if (normalizedSignal.includes("BUY")) {
        return "positive";
    }

    if (normalizedSignal.includes("SELL")) {
        return "negative";
    }

    return "";
};

const getStatusClassName = (status) => {
    const normalizedStatus = String(status || "").trim().toUpperCase();

    if (normalizedStatus === "ABOVE") {
        return "positive";
    }

    if (normalizedStatus === "BELOW") {
        return "negative";
    }

    return "";
};

const getDateSortValue = (value) => {
    if (!value || typeof value !== "string") {
        return 0;
    }

    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getMonthlyMidValue = (row) => {
    const monthlyHigh = Number(row?.monthlyHigh);
    const monthlyLow = Number(row?.monthlyLow);

    if (!Number.isFinite(monthlyHigh) || !Number.isFinite(monthlyLow)) {
        return null;
    }

    return (monthlyHigh + monthlyLow) / 2;
};

const getMonthlyMidDifferencePercentage = (row) => {
    const monthlyMid = getMonthlyMidValue(row);
    const ltp = Number(row?.ltp);

    if (!Number.isFinite(monthlyMid) || !Number.isFinite(ltp) || monthlyMid === 0) {
        return null;
    }

    return ((ltp - monthlyMid) / monthlyMid) * 100;
};

const isNearMonthlyMid = (row, thresholdPercentage = 1) => {
    const differencePercentage = getMonthlyMidDifferencePercentage(row);

    return differencePercentage != null && Math.abs(differencePercentage) <= thresholdPercentage;
};

const getColumnValue = (row, columnKey) => {
    if (columnKey === "monthlyMid") {
        return getMonthlyMidValue(row);
    }

    return row?.[columnKey];
};

const getCellClassName = (row, columnKey) => {
    const classNames = [];
    const columnValue = getColumnValue(row, columnKey);

    if (columnKey === "signal") {
        const signalClassName = getSignalClassName(columnValue);

        if (signalClassName) {
            classNames.push(signalClassName);
        }
    } else if (columnKey === "wstatus" || columnKey === "mstatus") {
        const statusClassName = getStatusClassName(columnValue);

        if (statusClassName) {
            classNames.push(statusClassName);
        }
    }

    if ((columnKey === "ltp" || columnKey === "monthlyMid") && isNearMonthlyMid(row)) {
        classNames.push("near-monthly-mid");
    }

    return classNames.join(" ");
};

const renderCellContent = (row, columnKey) => {
    const columnValue = getColumnValue(row, columnKey);

    if (columnKey !== "monthlyMid") {
        return formatValue(columnValue);
    }

    const differencePercentage = getMonthlyMidDifferencePercentage(row);
    const differenceClassName =
        differencePercentage > 0 ? "positive" : differencePercentage < 0 ? "negative" : "";

    return (
        <div className="positional-mid-cell">
            <span>{formatValue(columnValue)}</span>
            <span className={`positional-mid-diff ${differenceClassName}`.trim()}>
                {formatSignedPercent(differencePercentage)}
            </span>
        </div>
    );
};

export default function Positional() {
    const headerRef = useRef(null);
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [tableHeaderOffset, setTableHeaderOffset] = useState(0);
    const [sortConfig, setSortConfig] = useState({
        key: "",
        direction: "",
    });

    useEffect(() => {
        let isMounted = true;

        const fetchPositionalData = async () => {
            try {
                setIsLoading(true);
                const response = await axios.get(POSITIONAL_API_URL);
                const nextRows = getRowsFromPayload(response.data);

                if (!isMounted) {
                    return;
                }

                setRows(nextRows);
                setError("");
            } catch (fetchError) {
                if (!isMounted) {
                    return;
                }

                setRows([]);
                setError("Unable to load positional data.");
                console.error("Failed to fetch positional data:", fetchError);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        fetchPositionalData();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        const updateTableHeaderOffset = () => {
            setTableHeaderOffset(headerRef.current?.offsetHeight ?? 0);
        };

        updateTableHeaderOffset();
        window.addEventListener("resize", updateTableHeaderOffset);

        return () => {
            window.removeEventListener("resize", updateTableHeaderOffset);
        };
    }, []);

    const headerSource = rows[0] ?? {};
    const { date, time } = getDateTimeParts(headerSource.lastUpdated);
    const emptyMessage = isLoading
        ? "Loading positional data..."
        : error || "No positional data found.";

    const handleSort = (columnKey) => {
        if (!sortableDateColumns.has(columnKey)) {
            return;
        }

        setSortConfig((currentConfig) => {
            if (currentConfig.key !== columnKey) {
                return {
                    key: columnKey,
                    direction: "asc",
                };
            }

            if (currentConfig.direction === "asc") {
                return {
                    key: columnKey,
                    direction: "desc",
                };
            }

            return {
                key: "",
                direction: "",
            };
        });
    };

    const sortedRows = !sortableDateColumns.has(sortConfig.key)
        ? rows
        : [...rows].sort((leftRow, rightRow) => {
            const leftValue = getDateSortValue(leftRow[sortConfig.key]);
            const rightValue = getDateSortValue(rightRow[sortConfig.key]);

            if (leftValue === rightValue) {
                return 0;
            }

            if (sortConfig.direction === "asc") {
                return leftValue - rightValue;
            }

            return rightValue - leftValue;
        });

    const getSortIndicator = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return "";
        }

        return sortConfig.direction === "asc" ? " ↑" : " ↓";
    };

    return (
        <div
            className="positional-page"
            style={{ "--table-header-offset": `${tableHeaderOffset}px` }}
        >
            <header className="header" ref={headerRef}>
                <nav className="nav-container">
                    <div className="header-flex">
                        <div className="logo-container">
                            <img
                                src="/assets/images/noptionwave-logo-1.png"
                                className="header-logo"
                                alt="noptionwave logo"
                            />
                        </div>

                        <div className="right-nav">
                            <span className="header-line">Date : {date}</span>
                            <span className="header-line">Time : {time}</span>
                        </div>
                    </div>
                </nav>
            </header>

            <div className="table-container">
                <table className="responsive-table table-bordered text-center sticky-header">
                    <thead>
                        <tr>
                            {tableColumns.map((column) => (
                                <th
                                    key={column.key}
                                    className={sortableDateColumns.has(column.key) ? "sortable-header" : ""}
                                    onClick={() => sortableDateColumns.has(column.key) && handleSort(column.key)}
                                >
                                    {column.label}
                                    {getSortIndicator(column.key)}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {sortedRows.length > 0 ? (
                            sortedRows.map((row, index) => (
                                <tr key={`${row.symbol ?? "symbol"}-${index}`}>
                                    {tableColumns.map((column) => (
                                        <td
                                            key={column.key}
                                            className={getCellClassName(row, column.key)}
                                        >
                                            {renderCellContent(row, column.key)}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={tableColumns.length}>{emptyMessage}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <footer className="page-footer">noptionwave © 2025</footer>
        </div>
    );
}
